import {
  AccountTree as MuiAccountTreeIcon,
  AttachMoney as MuiAttachMoneyIcon,
  FilterList as MuiFunnelIcon,
  People as MuiPeopleIcon,
  ShowChart as MuiShowChartIcon,
} from '@mui/icons-material';
import { Box } from '@mui/material';

import { adminItems, transactionItems } from './navigation/navItems';
import {
  CollapsedNavItem,
  ExpandedNavGroup,
  NavDivider,
  SubNavItem,
} from './navigation/NavPrimitives';
import { InboxIcon } from './navigation/SidebarIcons';
import { SidebarShell } from './SidebarShell';
import { UserSwitcher } from './UserSwitcher';

import { ViewSwitcher } from '@/components/navigation/ViewSwitcher';
import { useDashboardLayout } from '@/context/DashboardLayoutProvider';
import { useUploaderContext } from '@/context/UploaderProvider';
import { useUnreadMessages } from '@/hooks/useUnreadMessages';
import { useUserRole } from '@/hooks/useUserRole';

const adminClientsItem: SubNavItem = {
  path: '/admin/advisor-clients',
  label: 'Advisor-Client',
  icon: <MuiPeopleIcon sx={{ fontSize: 15 }} />,
};

const adminMessagesItem = (badge?: number): SubNavItem => ({
  path: '/admin/inbox',
  label: 'Messages',
  icon: <InboxIcon size={15} />,
  badge,
});

export const AdminSidebar = () => {
  const { isLeftSidebarCollapsed } = useDashboardLayout();
  const { totalUnread } = useUnreadMessages();
  const { stepProcessing } = useUploaderContext();
  const { canAccessAdvancedData } = useUserRole();
  const isProcessing = stepProcessing >= 1 && stepProcessing < 5;

  const dataItems: SubNavItem[] = [
    ...(canAccessAdvancedData
      ? [
          {
            path: '/admin/logic',
            label: 'Logic',
            icon: <MuiAccountTreeIcon sx={{ fontSize: 15 }} />,
          },
          {
            path: '/admin/infracosts',
            label: 'Infra',
            icon: <MuiAttachMoneyIcon sx={{ fontSize: 15 }} />,
          },
        ]
      : []),
    { path: '/admin/kpis', label: 'KPIs', icon: <MuiShowChartIcon sx={{ fontSize: 15 }} /> },
    {
      path: '/admin/acquisitions',
      label: 'Performance',
      icon: <MuiFunnelIcon sx={{ fontSize: 15 }} />,
    },
  ];

  const adminItemsWithProcessing = adminItems.map((item) =>
    item.path === '/admin/properties' ? { ...item, processing: isProcessing } : item,
  );

  const manageItems = [adminClientsItem, adminMessagesItem(totalUnread || undefined)];

  if (isLeftSidebarCollapsed) {
    return (
      <SidebarShell
        footer={
          <>
            <Box
              sx={{
                mx: '10px',
                mt: '8px',
                mb: '4px',
                borderTop: '1px solid var(--tx-line)',
              }}
            />
            <UserSwitcher collapsed showEngagement={false} />
          </>
        }>
        {adminItemsWithProcessing.map((item) => (
          <CollapsedNavItem
            key={item.path}
            path={item.path}
            label={item.label}
            icon={item.icon}
            disabled={item.disabled}
            external={item.external}
            badge={item.badge}
          />
        ))}
        <NavDivider />
        {manageItems.map((item) => (
          <CollapsedNavItem
            key={item.path}
            path={item.path}
            label={item.label}
            icon={item.icon}
            disabled={item.disabled}
            external={item.external}
            badge={item.badge}
          />
        ))}
        <NavDivider />
        {dataItems.map((item) => (
          <CollapsedNavItem
            key={item.path}
            path={item.path}
            label={item.label}
            icon={item.icon}
            disabled={item.disabled}
            external={item.external}
            badge={item.badge}
          />
        ))}
        <NavDivider />
        {transactionItems.map((item) => (
          <CollapsedNavItem
            key={item.path}
            path={item.path}
            label={item.label}
            icon={item.icon}
            disabled={item.disabled}
            external={item.external}
          />
        ))}
      </SidebarShell>
    );
  }

  return (
    <SidebarShell
      footer={
        <>
          <Box sx={{ px: '12px', pb: '8px' }}>
            <ViewSwitcher />
          </Box>
          <Box sx={{ pb: '20px' }}>
            <UserSwitcher showEngagement={false} />
          </Box>
        </>
      }>
      <Box sx={{ display: 'flex', flexDirection: 'column', px: '6px', py: 0, mt: '10px' }}>
        <ExpandedNavGroup label="Admin" items={adminItemsWithProcessing} />
        <ExpandedNavGroup label="View/Manage" items={manageItems} />
        <ExpandedNavGroup label="Data" items={dataItems} />
        <ExpandedNavGroup
          label="Transaction"
          items={transactionItems}
          collapsible
          defaultCollapsed
        />
      </Box>
    </SidebarShell>
  );
};
